import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import StateManager, {
  ActionMeta,
  components,
  OptionsType,
  OptionTypeBase,
  ValueContainerProps,
  ValueType,
} from 'react-select';

import { useClickEscape } from '@dtci/triton/hooks';

import MultiSelect, { MultiSelectProps } from './MultiSelect';

import './MultiSelectPopout.scss';

export type MultiSelectPopoutProps<T extends OptionTypeBase> =
  MultiSelectProps<T> & {
    showCheckboxes?: boolean;
    showSelectAllOption?: boolean;
    groupSelected?: boolean;
    summary?: boolean;
    closeMenuOnScroll?: boolean;
  };

// Handles rendering the value in the Select
const ValueContainer = <T extends OptionTypeBase>({
  children,
  ...otherProps
}: ValueContainerProps<T, true>): JSX.Element => {
  const { hasSummary, placeholder, icon } = otherProps.selectProps;

  const values = otherProps.getValue();
  const hasValue = Boolean(values.length);
  const valueString = values.map((option) => option.label).join(', ');

  const className = classNames('sb-multiselect_popout_value-container', {
    'sb-multiselect_popout_value-placeholder': !hasValue,
  });

  // Show comma separated list or summary
  // Show placeholder (we use our own, since while the user is typing in the underlying input, we want to keep the placeholder active until selection)
  return (
    <components.ValueContainer {...otherProps}>
      <div className={className}>
        {icon && <>{icon}</>}
        {hasValue && hasSummary && <>{values.length} selected</>}
        {hasValue && !hasSummary && <>{valueString}</>}
        {!hasValue && <>{placeholder}</>}
      </div>
      {children}
    </components.ValueContainer>
  );
};

// Basis: https://github.com/JedWatson/react-select/issues/3111#issuecomment-470911304
const MultiSelectPopout = <
  T extends OptionTypeBase = {
    value: string;
    label: string;
    isDisabled?: boolean;
  },
>({
  className,
  onChange,
  summary,
  groupSelected,
  customNoOptionsMessage,
  showCheckboxes,
  showSelectAllOption,
  closeMenuOnScroll,
  closeMenuOnEscape = true,
  ...otherProps
}: MultiSelectPopoutProps<T>): JSX.Element => {
  const innerListRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<StateManager<T, true>>(null);
  const [shouldOpen, setShouldOpen] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const [values, setValues] = useState<ValueType<T, true>>(
    otherProps.value || [],
  );
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState(otherProps.options || []);

  const wrapperClassNames = useMemo(
    () =>
      classNames('sb-multiselect-popout__wrapper', {
        'sb-multiselect-popout--focused': isMenuOpen,
        'sb-multiselect-popout--should-close': shouldClose,
      }),
    [isMenuOpen, shouldClose],
  );

  const multiSelectClassNames = useMemo(
    () =>
      classNames(
        'sb-multiselect-popout',
        {
          'sb-multiselect-popout__group-selected': groupSelected,
        },
        className,
      ),
    [className, groupSelected],
  );

  const onSelectChange = useCallback(
    (selections: ValueType<T, true>, actionMeta: ActionMeta<T>) => {
      setValues(selections);
      if (onChange) onChange(selections, actionMeta);
    },
    [onChange],
  );

  // Need to allow for updates to the options prop for async handling
  useEffect(() => {
    if (otherProps.options) {
      setOptions(otherProps.options);
    }
  }, [otherProps.options]);

  useEffect(() => {
    if (groupSelected) {
      let selected: OptionsType<T> = [];
      let unselected: OptionsType<T> = [];

      const originalOptions = otherProps.options as OptionsType<T>; //assume group was not passed in

      unselected = originalOptions.filter((filter) =>
        values ? !values.includes(filter) : true,
      );
      selected = originalOptions.filter((filter) =>
        values ? values.includes(filter) : false,
      );

      setOptions([
        { label: 'Selected', options: selected },
        { label: 'Unselected', options: unselected },
      ]);
    }
  }, [groupSelected, otherProps.options, values]);

  useEffect(() => {
    if (isMenuOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMenuOpen]);

  const handleMouseDown = (e: MouseEvent | Event): void => {
    const container = containerRef.current;
    const menu = innerListRef.current;

    const menuContains =
      e.target instanceof Element && e.target.id
        ? menu?.querySelector('#' + e.target.id)
        : menu?.contains(e.target as Element);

    if (
      (!(e.target instanceof Element) ||
        (container?.classList.contains('sb-multiselect-popout--should-close') &&
          !menuContains) ||
        (!container?.contains(e.target as Element) && !menuContains)) &&
      // react-select targets the document body when scroll lock is enabled
      (e.target as Element).tagName !== 'BODY'
    ) {
      setShouldOpen(true);
      setShouldClose(false);
      setIsMenuOpen(false);
      setInputValue('');
      const activeElement = document.activeElement as HTMLElement;
      // Prevents issues with focus on react-select by clicking on the input again to close it
      if (container?.contains(activeElement)) {
        activeElement.blur();
      }
    } else {
      setShouldClose(true);
    }
  };

  // Handles swapping the state, since focus is being controlled outside of react-select
  // https://github.com/facebook/react/issues/20325
  useEffect(() => {
    document.addEventListener('click', handleMouseDown, { capture: true });
    document.addEventListener('touchend', handleMouseDown, { capture: true });
    return () => {
      document.removeEventListener('click', handleMouseDown, { capture: true });
      document.removeEventListener('touchend', handleMouseDown, {
        capture: true,
      });
    };
  }, []);

  // Need to allow for updates to the values prop for controlled values
  useEffect(() => {
    setValues(otherProps.value);
  }, [otherProps.value]);

  useClickEscape(() => {
    if (closeMenuOnEscape) {
      setShouldOpen(true);
      setShouldClose(false);
      setIsMenuOpen(false);
    }
  }, [setShouldOpen, setShouldClose, setIsMenuOpen, closeMenuOnEscape]);

  return (
    <div
      ref={containerRef}
      className={wrapperClassNames}
      data-testid="multiSelectPopout"
    >
      <MultiSelect<T>
        {...otherProps}
        isClearable={
          'isClearable' in otherProps ? otherProps.isClearable : true
        }
        components={{
          ...otherProps.components,
          ValueContainer,
          // Normal placeholder disappears while typing, lets ditch it
          Placeholder: () => {
            return <></>;
          },
          // Don't want to render pills
          MultiValue: () => {
            return <></>;
          },
          // Don't want to render pill wrapper
          MultiValueContainer: () => {
            return <></>;
          },
        }}
        closeMenuOnScroll={(e) => {
          if (
            !closeMenuOnScroll ||
            (e.target as HTMLElement)?.classList?.contains(
              'sb-multiselect__menu-list',
            )
          ) {
            return;
          }
          setShouldOpen(true);
          setShouldClose(false);
          setIsMenuOpen(false);
          setInputValue('');
        }}
        // Track inner input focus
        onFocus={() => {
          if (shouldOpen) {
            setIsMenuOpen(true);
          }
        }}
        // Track inner input focus
        onMenuInputFocus={() => {
          setIsMenuOpen(true);
        }}
        // Keep menu open while inner input is focused
        menuIsOpen={isMenuOpen}
        isSearchable={false}
        onChange={(selections, actionMeta) => {
          if (actionMeta.action === 'clear') {
            setShouldOpen(false);
          } else {
            setShouldOpen(true);
          }
          onSelectChange(selections, actionMeta);
          // Push focus back to input on change
          // https://github.com/JedWatson/react-select/issues/1309#issuecomment-739601300
          setTimeout(() => {
            // If menu is currently open, push focus back to the search input
            isMenuOpen && inputRef.current && inputRef.current.focus();

            // If menu is currently closed, and the values have been cleared, remove focus and reset for the next interaction
            if (!isMenuOpen && actionMeta.action === 'clear') {
              selectRef.current && selectRef.current.select.blur();
              setShouldOpen(true);
            }
          }, 1);
        }}
        value={values}
        options={options}
        className={multiSelectClassNames}
        showInputPopout
        showCheckboxes={showCheckboxes}
        showSelectAllOption={showSelectAllOption}
        hasSummary={summary}
        selectRef={selectRef}
        inputRef={inputRef}
        innerListRef={innerListRef}
        inputValue={inputValue}
        onInputChange={(val) => setInputValue(val)}
        customNoOptionsMessage={customNoOptionsMessage}
      />
    </div>
  );
};

export default MultiSelectPopout;
